import classname from "classnames";
import React, { PureComponent, ReactNode, Suspense } from "react";
import { State as ReactBurgerState } from "react-burger-menu";
import { RouteComponentProps } from "react-router-dom";
import styled, { css } from "styled-components";

import theme, { media } from "../../../../../theme";
import Icon from "../../../../Icon";
import AppMenu from "../AppMenu";

interface IHeaderTextProps {
  hasSubTitle: boolean;
}

const HeaderTitle = styled.section`
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: 34px;
  margin: ${theme.margins.marginBase};

  ${media.laptop} {
    margin: 0;
  }
`;

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  ${media.laptop} {
    display: block;
  }
`;

const HeaderTextStyle = css`
  display: flex;
  justify-content: center;
  align-content: center;
  flex-grow: 1;
  text-align: center;
  line-height: 1.2;
  transform: translateY(-3px);
  color: ${theme.colors.black};
`;

const HeaderText = styled.h1<IHeaderTextProps>`
  ${HeaderTextStyle}
  font-size: ${(props: IHeaderTextProps): string =>
    props.hasSubTitle ? theme.fontSizes.big : theme.fontSizes.huge};
  font-family: ${(props: IHeaderTextProps): string =>
    props.hasSubTitle ? theme.fonts.subtitle : theme.fonts.title};
  margin: 0;

  ${media.laptop} {
    font-family: ${theme.fonts.regular};
    font-size: 30px;
    font-weight: 400;
    text-transform: uppercase;
    letter-spacing: 8px;
    padding: 30px 0;
    border-bottom: 1px solid ${theme.colors.veryLightGrey};
    transform: initial;
    justify-content: initial;
  }
`;

const HeaderSubTitleText = styled.section`
  ${HeaderTextStyle}
  font-size: 18px;
  font-family: ${theme.fonts.regular};
  margin-top: 4px;
`;

const IconWrapper = styled.div`
  display: flex;
  flex: 1;
`;

const IconWrapperLeft = styled(IconWrapper)`
  justify-content: flex-start;
`;

const IconWrapperRight = styled(IconWrapper)`
  justify-content: flex-end;
`;

export interface IProps extends RouteComponentProps {
  canGoBack?: boolean;
  brandId?: string;
  hamburgerMenu?: boolean;
  headerRightIcon?: ReactNode;
  headerSibling?: ReactNode;
  pageType?: string;
  subTitle?: string;
  title?: string;
  className?: string;
}

interface IState {
  isAppMenuOpen: boolean;
}

class SubHeader extends PureComponent<IProps, IState> {
  public state: IState = {
    isAppMenuOpen: false,
  };

  public goBack(): void {
    this.props.history.goBack();
  }

  public openAppMenu = (): void => {
    this.setState({ isAppMenuOpen: true });
  };

  public render(): ReactNode {
    const {
      title,
      hamburgerMenu,
      headerRightIcon,
      headerSibling,
      subTitle,
      className,
    } = this.props;

    return (
      <>
        <Suspense fallback={<div />}>
          <AppMenu
            isOpen={this.state.isAppMenuOpen}
            toggleMenu={this.toggleAppMenu}
          />
        </Suspense>

        <HeaderTitle className={classname(className, "page-title")}>
          {(this.props.canGoBack || hamburgerMenu) && (
            <IconWrapperLeft>
              {this.renderGoBackButton()}

              {hamburgerMenu && (
                <Icon name="menu" size={24} onClick={this.openAppMenu} />
              )}
            </IconWrapperLeft>
          )}

          {(title || subTitle) && (
            <TitleWrapper className="container">
              {title && (
                <HeaderText hasSubTitle={!!subTitle}>{title}</HeaderText>
              )}

              {subTitle && <HeaderSubTitleText>{subTitle}</HeaderSubTitleText>}
            </TitleWrapper>
          )}

          {headerRightIcon && (
            <IconWrapperRight>{headerRightIcon}</IconWrapperRight>
          )}
        </HeaderTitle>
        {headerSibling}
      </>
    );
  }

  public renderGoBackButton(): ReactNode {
    const { canGoBack } = this.props;
    if (canGoBack) {
      return (
        <Icon
          name="chevron-gauche"
          size={24}
          onClick={(): void => this.goBack()}
        />
      );
    }

    return null;
  }

  public toggleAppMenu = (state: ReactBurgerState): void => {
    this.setState({
      isAppMenuOpen: state.isOpen,
    });
  };
}

export default SubHeader;
